import {
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../api/client";
import type { CatalogCard } from "../api/client";
import { useMe } from "../api/useMe";
import { pixelTheme } from "../theme/pixel";
import { primaryBtn, ghostBtn, withBtnStyle } from "../theme/styles";
import { E32 } from "../theme/endesga32";
import { useResponsiveLayout } from "../logic/useResponsiveLayout";
import { installLandingAnimations } from "../theme/landingAnimations.css";
import { InfinityFanHero } from "../components/landing/InfinityFanHero";
import { Logo } from "../components/Logo";
import { CardMarquee } from "../components/landing/CardMarquee";
import { CardZoomModal } from "../components/landing/CardZoomModal";
import { FactionTile } from "../components/landing/FactionTile";

// Card ids hand-picked from base_set for the hero fan: the most
// interesting uncommon and legendary in the three default-unlocked
// factions (humans / goblins / dwarves). Elves and orcs are gated
// behind world progression and aren't shown on the landing page.
// If either is ever renamed in base_set, point these constants at
// the new id.
const HERO_UNCOMMON_ID = "knight_errant";
const HERO_LEGENDARY_ID = "krennig_the_master_thief";

const FACTION_TAGLINES: Array<{
  faction: "humans" | "goblins" | "dwarves" | "orcs" | "elves";
  tagline: string;
}> = [
  { faction: "humans", tagline: "Disciplined & Relentless" },
  { faction: "goblins", tagline: "Reckless & Explosive" },
  { faction: "dwarves", tagline: "Staid & Methodical" },
  { faction: "orcs", tagline: "Grim & Brutal" },
  { faction: "elves", tagline: "Lyrical & Patient" },
];

export function LandingPage() {
  const navigate = useNavigate();
  const me = useMe();
  const layout = useResponsiveLayout();
  const [cards, setCards] = useState<CatalogCard[]>([]);
  const [zoomed, setZoomed] = useState<CatalogCard | null>(null);

  // Install the keyframes the moment the landing page mounts. Idempotent.
  useEffect(() => {
    installLandingAnimations();
  }, []);

  useEffect(() => {
    let cancelled = false;
    api
      .getLandingSampleCards(28, [HERO_UNCOMMON_ID, HERO_LEGENDARY_ID])
      .then(({ cards: list }) => {
        if (!cancelled) setCards(list);
      })
      .catch(() => {
        // Silent: the page renders perfectly fine with an empty stream.
        // The marquee just won't paint. The hero illustration also
        // gracefully falls back to placeholders.
      });
    return () => {
      cancelled = true;
    };
  }, []);


  const heroUncommon = useMemo(
    () => cards.find((c) => c.id === HERO_UNCOMMON_ID) ?? null,
    [cards],
  );
  const heroLegendary = useMemo(
    () => cards.find((c) => c.id === HERO_LEGENDARY_ID) ?? null,
    [cards],
  );

  // Even rows feel best with at least ~8 distinct cards (so the loop
  // isn't visibly small). If the server returned fewer, we still show
  // what we have — the marquee duplicates internally.
  const row1 = cards;
  const row2 = useMemo(() => [...cards].reverse(), [cards]);

  function goPlay() {
    if (me) {
      navigate("/lobby");
    } else {
      navigate("/login");
    }
  }

  // Headings shrink one notch when narrow; the frontend CSS allowlist
  // forbids CSS scaling functions, so the branch is in JS.
  const heroH1FontSize = layout.narrow ? 34 : 52;
  const bandH2FontSize = layout.narrow ? 20 : 26;
  const closerH2FontSize = layout.narrow ? 22 : 32;

  return (
    <div
      style={{
        position: "relative",
        background: pixelTheme.pageBg,
        color: pixelTheme.ink,
        fontFamily: pixelTheme.fontBody,
        // The app shell pins `#root` to one viewport with overflow
        // hidden, so the landing page has to be its own scroll
        // container.
        height: "100%",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      <ParchmentWeaveBackdrop />

      <Header onPlay={goPlay} narrow={layout.narrow} />

      <HeroSection
        heroH1FontSize={heroH1FontSize}
        narrow={layout.narrow}
        onPlay={goPlay}
        uncommon={heroUncommon}
        legendary={heroLegendary}
        onCardClick={setZoomed}
      />

      <section id="pool" style={poolBandStyle}>
        <Shell>
          <Eyebrow color={E32.amber}>An Ever-Growing Pool</Eyebrow>
          <h2
            style={{
              fontFamily: pixelTheme.fontDisplay,
              fontSize: bandH2FontSize,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: E32.parchment,
              margin: "14px 0 0",
              textAlign: "center",
            }}
          >
            Endless New Cards
          </h2>
          <p
            style={{
              fontFamily: pixelTheme.fontBody,
              fontSize: 19,
              color: E32.mist,
              maxWidth: "40ch",
              margin: "14px auto 0",
              lineHeight: 1.4,
              textAlign: "center",
            }}
          >
            Fresh cards are generated and automatically balanced near
            50% win-rate before they ship.{" "}
            <b style={{ color: E32.amber, fontWeight: 400 }}>
              Every card you open has never been seen before.
            </b>
          </p>
        </Shell>

        <div style={{ marginTop: 34 }}>
          <CardMarquee cards={row1} onCardClick={setZoomed} />
        </div>
        <div style={{ marginTop: 16 }}>
          <CardMarquee cards={row2} reverse onCardClick={setZoomed} />
        </div>
      </section>

      <section id="factions" style={factionsSectionStyle}>
        <Shell>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <Eyebrow>The Five Realms of High Fantasia</Eyebrow>
            <h2
              style={{
                fontFamily: pixelTheme.fontDisplay,
                fontSize: bandH2FontSize,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: pixelTheme.ink,
                margin: "14px 0 0",
              }}
            >
              Choose Your Faction
            </h2>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: layout.narrow
                ? "repeat(2, 1fr)"
                : "repeat(5, 1fr)",
              gap: 16,
            }}
          >
            {FACTION_TAGLINES.map((f) => (
              <FactionTile
                key={f.faction}
                faction={f.faction}
                tagline={f.tagline}
              />
            ))}
          </div>
        </Shell>
      </section>

      <section style={{ padding: "6px 0 80px" }}>
        <Shell>
          <div style={{ textAlign: "center" }}>
            <h2
              style={{
                fontFamily: pixelTheme.fontDisplay,
                fontSize: closerH2FontSize,
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: pixelTheme.ink,
                margin: "0 0 8px",
                lineHeight: 1.25,
              }}
            >
              The Infiniverse
              <br />
              Is{" "}
              <span style={{ color: pixelTheme.accent }}>Waiting</span>
            </h2>
            <p
              style={{
                fontFamily: pixelTheme.fontBody,
                fontSize: 19,
                color: pixelTheme.inkDim,
                margin: "0 auto 30px",
                maxWidth: "34ch",
              }}
            >
              Begin your journey in an infinite world.
            </p>
            <PlayButton onClick={goPlay} />
          </div>
        </Shell>
      </section>

      <CardZoomModal card={zoomed} onClose={() => setZoomed(null)} />
    </div>
  );
}

// ── Sub-pieces ───────────────────────────────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        maxWidth: 1120,
        margin: "0 auto",
        padding: "0 28px",
        position: "relative",
        zIndex: 1,
      }}
    >
      {children}
    </div>
  );
}

function Eyebrow({
  children,
  color,
}: {
  children: React.ReactNode;
  color?: string;
}) {
  return (
    <span
      style={{
        fontFamily: pixelTheme.fontDisplay,
        fontSize: 10,
        letterSpacing: "0.22em",
        textTransform: "uppercase",
        color: color ?? pixelTheme.inkFaint,
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      {children}
    </span>
  );
}

function Header({ onPlay, narrow }: { onPlay: () => void; narrow: boolean }) {
  const ghost = ghostBtn(pixelTheme);
  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 30,
        background: pixelTheme.pageBg,
        borderBottom: `2px solid ${pixelTheme.ink}`,
      }}
    >
      <div
        style={{
          maxWidth: 1120,
          margin: "0 auto",
          padding: "0 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 62,
        }}
      >
        <Logo />
        {!narrow && (
          <nav
            style={{
              display: "flex",
              alignItems: "center",
              gap: 26,
            }}
          >
            <a
              href="#pool"
              style={{
                fontFamily: pixelTheme.fontDisplay,
                fontSize: 10,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: pixelTheme.inkDim,
                textDecoration: "none",
              }}
            >
              The Pool
            </a>
            <a
              href="#factions"
              style={{
                fontFamily: pixelTheme.fontDisplay,
                fontSize: 10,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: pixelTheme.inkDim,
                textDecoration: "none",
              }}
            >
              Factions
            </a>
            <button
              className={ghost.className}
              style={ghost.style}
              onClick={onPlay}
            >
              Play
            </button>
          </nav>
        )}
        {narrow && (
          <button
            className={ghost.className}
            style={ghost.style}
            onClick={onPlay}
          >
            Play
          </button>
        )}
      </div>
    </header>
  );
}

function HeroSection({
  heroH1FontSize,
  narrow,
  onPlay,
  uncommon,
  legendary,
  onCardClick,
}: {
  heroH1FontSize: number;
  narrow: boolean;
  onPlay: () => void;
  uncommon: CatalogCard | null;
  legendary: CatalogCard | null;
  onCardClick: (card: CatalogCard) => void;
}) {
  return (
    <section style={{ padding: "64px 0 76px", overflow: "hidden" }}>
      <div
        style={{
          maxWidth: 1120,
          margin: "0 auto",
          padding: "0 28px",
          display: "grid",
          gridTemplateColumns: narrow ? "1fr" : "1.05fr 0.95fr",
          gap: 40,
          alignItems: "center",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ order: narrow ? 2 : undefined }}>
          <Eyebrow>Generative Trading Card Game</Eyebrow>
          <h1
            style={{
              fontFamily: pixelTheme.fontDisplay,
              textTransform: "uppercase",
              color: pixelTheme.ink,
              margin: "18px 0 0",
              letterSpacing: "0.06em",
              lineHeight: 1.12,
              fontSize: heroH1FontSize,
            }}
          >
            The Game
            <br />
            That Never
            <br />
            <span style={{ color: pixelTheme.accent }}>Ends</span>
          </h1>
          <p
            style={{
              fontFamily: pixelTheme.fontBody,
              fontSize: 21,
              lineHeight: 1.45,
              color: pixelTheme.ink,
              maxWidth: narrow ? "none" : "30ch",
              margin: "22px 0 0",
            }}
          >
            A trading card game where{" "}
            <b style={{ color: pixelTheme.accent, fontWeight: 400 }}>
              every card you find is unique.
            </b>{" "}
            Conjured from bubble worlds across the Infiniverse —
            collect it, build it, battle it.
          </p>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 16,
              marginTop: 30,
              flexWrap: "wrap",
            }}
          >
            <PlayButton onClick={onPlay} />
            <div
              style={{
                fontFamily: pixelTheme.fontDisplay,
                fontSize: 9,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: pixelTheme.inkFaint,
                lineHeight: 1.7,
              }}
            >
              Free to play
              <br />
              Claim your 3 free starter decks
            </div>
          </div>
        </div>

        <div style={{ order: narrow ? 1 : undefined }}>
          <InfinityFanHero
            uncommon={uncommon}
            legendary={legendary}
            onCardClick={onCardClick}
          />
        </div>
      </div>
    </section>
  );
}

function PlayButton({ onClick }: { onClick: () => void }) {
  const big = withBtnStyle(primaryBtn(pixelTheme), {
    padding: "16px 26px",
    fontSize: 12,
  });
  return (
    <button className={big.className} style={big.style} onClick={onClick}>
      Play Now
    </button>
  );
}

// Faint pixel-grid weave so the parchment background doesn't read as
// flat web-white. Fixed-position, pointer-events: none.
function ParchmentWeaveBackdrop() {
  return (
    <div
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 0,
        pointerEvents: "none",
        opacity: 0.5,
        backgroundImage: `
          repeating-linear-gradient(0deg, rgba(115, 62, 57, 0.045) 0 2px, transparent 2px 4px),
          repeating-linear-gradient(90deg, rgba(115, 62, 57, 0.045) 0 2px, transparent 2px 4px)
        `,
      }}
    />
  );
}

const poolBandStyle: CSSProperties = {
  position: "relative",
  background: E32.ink,
  borderTop: `3px solid ${E32.plum}`,
  borderBottom: `3px solid ${E32.plum}`,
  padding: "46px 0 50px",
  overflow: "hidden",
  textAlign: "center",
};

const factionsSectionStyle: CSSProperties = {
  padding: "70px 0 64px",
};
